import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM, HeadlineS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';

import { Games } from '../Games';
import { Props as GameProps } from '../Games/Game';

type Props = {
  displayName?: string;
  loading: boolean;
  loadPage: (direction: 'previous' | 'next') => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  pastGames: GameProps['game'][];
  futureGames: GameProps['game'][];
};

const StyledHorizontal = styled(SBHorizontal)`
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--c-nd-50);
  border-bottom: 1px solid var(--c-black);
  border-top-right-radius: var(--double-unit);
  border-top-left-radius: var(--double-unit);
`;
const LoadingContainer = styled.div`
  padding: var(--triple-unit);
  background: var(--c-nd-50);
  border-bottom-right-radius: var(--double-unit);
  border-bottom-left-radius: var(--double-unit);
`;

export const GamesNavigator = ({
  displayName,
  loading,
  hasNextPage,
  hasPreviousPage,
  loadPage,
  pastGames,
  futureGames,
}: Props) => {
  const { sport } = useSportContext();

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          {displayName ? (
            <FormattedMessage
              id="Seo.GamesNavigator.nameFixtures"
              defaultMessage="{displayName} fixtures"
              values={{ displayName }}
            />
          ) : (
            <FormattedMessage
              {...(sport === Sport.FOOTBALL
                ? navLabels.fixtures
                : glossary.games)}
            />
          )}
        </HeadlineS>
        <div>
          <StyledHorizontal>
            <IconButton
              small
              color="tertiary"
              icon={faChevronLeft}
              onClick={() => loadPage('previous')}
              disabled={!hasPreviousPage}
            />
            <BodyM as="p">
              <FormattedMessage
                {...(sport === Sport.FOOTBALL
                  ? navLabels.fixtures
                  : glossary.games)}
              />
            </BodyM>
            <IconButton
              small
              color="tertiary"
              icon={faChevronRight}
              onClick={() => loadPage('next')}
              disabled={!hasNextPage}
            />
          </StyledHorizontal>
          {loading ? (
            <LoadingContainer>
              <LoadingIndicator small />
            </LoadingContainer>
          ) : (
            <Games games={[...[...pastGames].reverse(), ...futureGames]} />
          )}
        </div>
      </Vertical>
    </FullWidthContainer>
  );
};
